/**
 * Admin V3 — RefundGuidanceModal (SC-PR-11)
 *
 * Advisory-only modal that lazy-fetches a refund recommendation from the
 * SC-PR-10 endpoint:
 *
 *   GET /admin/bookings/{booking_id}/refund-recommendation
 *       ?cancellation_reason_type={...}
 *
 * INVARIANTS (binding constraints from SC-PR-11 authorization):
 *   1. Modal is advisory-only.
 *   2. NO POST surface. The modal uses `useAdminQuery` (GET-only hook);
 *      it never imports `useAdminMutation`, `axios.post`, or any other
 *      write-path API.
 *   3. NO automatic refund execution. No "Process refund" button.
 *   4. NO automatic amount pre-fill. The "Open legacy flow" button
 *      navigates to `/admin/bookings/:id` with no URL query params.
 *      The legacy RefundModal in AdminControlTowerV2.js is unchanged
 *      and has no listener for any pre-fill query string.
 *   5. Existing refund / cancel / webhook / RBAC routes are not touched
 *      anywhere in this PR's diff.
 *
 * The operator picks the cancellation reason; the modal calls the engine;
 * the engine returns an advisory recommendation; the operator can choose
 * to navigate to the legacy refund flow OR close the modal. The operator
 * remains the final decision-maker.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ExternalLink } from 'lucide-react';

import AdvisoryBanner from '../../shared/components/AdvisoryBanner';
import RefundGuidanceCard from './RefundGuidanceCard';
import bookingDetailPath from './bookingDetailPath';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import usePermissions from '../../shared/hooks/usePermissions';
import { t } from '../../i18n';

// The 8 reason values are the union of CancellationReasonType (the 7
// enum members in admin_control_tower_v2.py:67-74) plus `no_show`,
// which is documented in the published RefundPolicyPage and modeled in
// the SC-PR-10 engine even though the operator-cancel enum does not
// yet include it. Order matches the canonical visual order in the
// engine's reason-handling rules.
const CANCELLATION_REASON_KEYS = [
  'customer_request',
  'driver_unavailable',
  'payment_failed',
  'duplicate_booking',
  'fraud_suspected',
  'operational',
  'other',
  'no_show',
];

const RefundGuidanceModal = ({ open, booking, onClose, locale = 'fr' }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();

  // Operator picks the cancellation reason inside the modal. The engine
  // requires this parameter; we do NOT default-pick a value, so the
  // operator must actively select one before the fetch fires.
  const [reasonType, setReasonType] = useState(null);

  const bookingId = booking?.id;
  const bookingRef = booking?.booking_reference || booking?.id?.substring(0, 8)?.toUpperCase() || '';

  // Lazy fetch: only fires when the modal is open AND a reason is selected.
  const enabled = Boolean(open && bookingId && reasonType);
  const endpoint = bookingId
    ? `/admin/bookings/${bookingId}/refund-recommendation`
    : null;
  const params = reasonType ? { cancellation_reason_type: reasonType } : {};
  const { data: recommendation, loading, error } = useAdminQuery(
    endpoint,
    params,
    { enabled }
  );

  const handleClose = () => {
    setReasonType(null);
    onClose?.();
  };

  // "Open booking detail" affordance. Routes through the canonical booking-detail
  // navigation authority (bookingDetailPath). Post-AV3 A-3 cutover it lands on the
  // V3-native detail page (/admin-v3/bookings/:id), where refund execution now
  // lives (AV3 A-2); on rollback (flag false) it falls back to legacy V2. No URL
  // query params — no automatic amount pre-fill — so the manual-entry constraint
  // is preserved by construction on either surface.
  const handleOpenLegacy = () => {
    if (!bookingId) return;
    navigate(bookingDetailPath(bookingId));
  };

  const canSeeLegacyButton = can('payment:refund');

  const subtitle = useMemo(() => {
    return t('bookings.refundGuidance.bookingRef', locale).replace(
      '{ref}',
      bookingRef
    );
  }, [bookingRef, locale]);

  const advisoryLines = useMemo(() => [
    t('bookings.refundGuidance.advisoryBanner.line1', locale),
    t('bookings.refundGuidance.advisoryBanner.line2', locale),
  ], [locale]);

  // The select component requires a non-empty string value; we use an
  // explicit sentinel for the "no reason chosen yet" state.
  const SENTINEL = '__pick__';
  const selectValue = reasonType || SENTINEL;
  const onReasonChange = (v) => {
    if (v === SENTINEL) {
      setReasonType(null);
    } else {
      setReasonType(v);
    }
  };

  return (
    <Dialog open={Boolean(open)} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent
        className="bg-white border-slate-200 max-w-2xl"
        data-testid="adminv3-refund-guidance-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-900">
            {t('bookings.refundGuidance.title', locale)}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            <span>{subtitle}</span>
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 border border-amber-200">
              {t('bookings.refundGuidance.subtitleAdvisory', locale)}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2 max-h-[60vh] overflow-y-auto">
          {/* Reason selector */}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
              {t('bookings.refundGuidance.reason.label', locale)}
            </label>
            <Select value={selectValue} onValueChange={onReasonChange}>
              {/*
                SC-PR-11A: explicit text/border/background colours override
                the shadcn-default theme tokens. Without these overrides the
                SelectTrigger's selected value rendered with the inherited
                `--foreground` which is washed out on the modal's
                bg-white DialogContent. Same fix applied to SelectContent
                and SelectItem to keep the dropdown panel readable.
              */}
              <SelectTrigger
                className="w-full bg-white text-slate-900 border-slate-300"
                data-testid="adminv3-refund-guidance-reason-select"
              >
                <SelectValue placeholder={t('bookings.refundGuidance.reason.placeholder', locale)} />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300">
                <SelectItem value={SENTINEL} disabled className="text-slate-400">
                  {t('bookings.refundGuidance.reason.placeholder', locale)}
                </SelectItem>
                {CANCELLATION_REASON_KEYS.map((key) => (
                  <SelectItem
                    key={key}
                    value={key}
                    data-testid={`adminv3-refund-guidance-reason-${key}`}
                    className="text-slate-900 focus:bg-slate-100 focus:text-slate-900"
                  >
                    {t(`bookings.refundGuidance.reason.${key}`, locale)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Recommendation card / states */}
          {!reasonType ? (
            <div
              className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-[13px] text-slate-500"
              data-testid="adminv3-refund-guidance-empty"
            >
              {t('bookings.refundGuidance.state.pickReason', locale)}
            </div>
          ) : loading ? (
            <div
              className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-[13px] text-slate-500"
              data-testid="adminv3-refund-guidance-loading"
            >
              {t('bookings.refundGuidance.state.loading', locale)}
            </div>
          ) : error ? (
            <div
              className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-[13px] text-rose-700"
              data-testid="adminv3-refund-guidance-error"
            >
              {t('bookings.refundGuidance.state.error', locale)}
              <div className="mt-1 text-[11px] text-rose-500">{String(error)}</div>
            </div>
          ) : recommendation ? (
            <RefundGuidanceCard recommendation={recommendation} locale={locale} />
          ) : null}

          {/* Mandatory advisory banner — always visible when modal is open */}
          <AdvisoryBanner
            lines={advisoryLines}
            tone="advisory"
            testId="adminv3-refund-guidance-advisory-banner"
          />
        </div>

        <DialogFooter className="flex gap-2 pt-3">
          {/*
            SC-PR-11A: explicit colour overrides on the Fermer button.
            The shadcn `variant="outline"` default has no text colour and
            inherits `--foreground` which is too pale on bg-white.
            hover: classes guarantee contrast survives the hover state.
          */}
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            data-testid="adminv3-refund-guidance-close"
            className="border-slate-300 text-slate-900 hover:bg-slate-100 hover:text-slate-900"
          >
            {t('bookings.refundGuidance.action.close', locale)}
          </Button>
          {canSeeLegacyButton ? (
            <Button
              type="button"
              onClick={handleOpenLegacy}
              data-testid="adminv3-refund-guidance-open-legacy"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('bookings.refundGuidance.action.openLegacy', locale)}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundGuidanceModal;
